/** Organization Group Settings — Models & Types */

/** Access level values for groups. */
export type AccessLevel =
  | 'full_access'
  | 'can_edit'
  | 'can_comment'
  | 'can_view';

/** Access level option for dropdown selection. */
export interface AccessLevelOption {
  value: AccessLevel;
  label: string;
}

/** Assignee embedded in a Group resource. */
export interface Assignee {
  id: number;
  name: string;
  email: string;
  /** Discriminator — present when used in space member context */
  member_type?: 'user' | 'group';
  /** Member count — present for group items */
  members_count?: number;
  /** Current permissions — present when loaded from space details */
  permissions?: { value: string; label: string }[];
  /** Pivot table ID — used for per-member permission API calls on spaces */
  resourcePermissionId?: number;
}

/** User with selection status for assignee multi-select (edit mode). */
export interface UserWithSelection {
  id: number;
  name: string;
  email: string;
  role: { value: string; label: string };
  invited_by: number | null;
  is_selected: boolean;
}

/** Group resource as returned by the API. */
export interface Group {
  id: number;
  name: string;
  description: string | null;
  access_level: { value: AccessLevel; label: string };
  assignees: Assignee[];
  created_at: string;
  updated_at: string;
}

/** Request body for POST create group. */
export interface CreateGroupRequest {
  name: string;
  description?: string | null;
  access_level: AccessLevel;
  assignee_ids: number[];
}

/** Request body for PUT update group. */
export interface UpdateGroupRequest {
  name: string;
  description?: string | null;
  access_level: AccessLevel;
  assignee_ids: number[];
}

/** Pagination metadata from the API. */
export interface GroupPaginationMeta {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

/** Paginated response for the groups list endpoint. */
export interface GroupListResponse {
  status: boolean;
  message: string;
  data: Group[];
  meta: GroupPaginationMeta;
}

/** Single group response from the API. */
export interface SingleGroupResponse {
  status: boolean;
  message: { title: string; description: string };
  data: Group;
}

/** User list with selection response (for edit mode assignee loading). */
export interface UserListWithSelectionResponse {
  status: boolean;
  message: { title: string; description: string };
  data: UserWithSelection[];
  meta: GroupPaginationMeta;
}

// ── Constants ──────────────────────────────────────────────────────────

/** Available access level options for dropdown. */
export const ACCESS_LEVEL_OPTIONS: AccessLevelOption[] = [
  { value: 'full_access', label: 'Full Access' },
  { value: 'can_edit', label: 'Can Edit' },
  { value: 'can_comment', label: 'Can Comment' },
  { value: 'can_view', label: 'Can View' },
];

/** Minimum length for group name. */
export const GROUP_NAME_MIN_LENGTH = 3;

/** Pattern for group name: must not start with whitespace. */
export const GROUP_NAME_PATTERN = /^[^\s].*/

/** Maximum length for group description. */
export const GROUP_DESCRIPTION_MAX_LENGTH = 1000;

/** Default page size for group list pagination. */
export const DEFAULT_GROUP_PAGE_SIZE = 25;

/** Available page size options. */
export const GROUP_PAGE_SIZE_OPTIONS = [25, 50, 100, 150] as const;
